import React, { useEffect, useMemo, useState } from "react";
import { getGameToolVersion } from "@/api/gameToolVersion";
import {
  getDataRuntimeConfig,
  type DataRuntimeConfig,
} from "../config/dataRuntime";
import {
  initializeTierListDataVersion,
  type GameToolVersionFetcher,
  type TierListDataVersionContext,
} from "./dataVersion";

interface DataVersionGateProps {
  children: (context: TierListDataVersionContext) => React.ReactNode;
}

export type DataVersionInitializationState =
  | { status: "pending" }
  | { status: "success"; context: TierListDataVersionContext }
  | { status: "error" };

export function createInitialDataVersionState(
  config: DataRuntimeConfig,
): DataVersionInitializationState {
  return config.source === "local"
    ? {
        status: "success",
        context: { version: config.version, dataRoot: config.versionRoot },
      }
    : { status: "pending" };
}

export async function loadDataVersionState(
  config: DataRuntimeConfig,
  location: Pick<Location, "search" | "hash">,
  getVersion: GameToolVersionFetcher,
): Promise<DataVersionInitializationState> {
  try {
    const context = await initializeTierListDataVersion(config, location, getVersion);
    return { status: "success", context };
  } catch {
    return { status: "error" };
  }
}

export const DataVersionGate: React.FC<DataVersionGateProps> = ({ children }) => {
  const config = useMemo(() => getDataRuntimeConfig(), []);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<DataVersionInitializationState>(() =>
    createInitialDataVersionState(config),
  );

  useEffect(() => {
    if (config.source === "local") return;

    let active = true;
    setState({ status: "pending" });
    loadDataVersionState(config, window.location, getGameToolVersion).then(nextState => {
      if (active) setState(nextState);
    });

    return () => {
      active = false;
    };
  }, [attempt, config]);

  if (state.status === "pending") {
    return <div className="page-loading">加载中...</div>;
  }

  if (state.status === "error") {
    return (
      <div className="page-loading" style={{ flexDirection: "column", gap: 12 }}>
        <p>数据初始化失败，请稍后重试</p>
        <button type="button" onClick={() => setAttempt(current => current + 1)}>
          重新加载
        </button>
      </div>
    );
  }

  return <>{children(state.context)}</>;
};
